// src/utils/ErrorMapper.ts

interface ErrorMapping {
  // Strings to look for in the raw error (e.g., "429", "context_length")
  keywords: string[];
  // The friendly message to show the business user
  userMessage: string;
}

const ERROR_LIBRARY: ErrorMapping[] = [
  {
    keywords: ["unsupported model"],
    userMessage:
      "The selected model isn't available. Please choose another model in the model settings.",
  },
  {
    keywords: ["unknown use case", "not configured"],
    userMessage:
      "This use case is no longer available. Please reload the page and choose another one.",
  },
  {
    keywords: ["429", "rate limit", "too many requests"],
    userMessage:
      "The AI service is currently receiving high traffic. Please wait a moment and try again.",
  },
  {
    keywords: ["401", "403", "unauthorized", "forbidden", "token expired"],
    userMessage:
      "Your session has expired or you do not have permission to access this resource. Please refresh the page or log in again.",
  },
  {
    keywords: ["context_length_exceeded", "maximum context", "token limit"],
    userMessage:
      "This conversation has become too long for the AI to process. Please start a 'New Chat' to continue.",
  },
  {
    keywords: [
      "content_filter",
      "responsible_ai",
      "policy violation",
      "flagged",
    ],
    userMessage:
      "The request or response was flagged by our safety filters. Please try rephrasing your question.",
  },
  {
    keywords: ["500", "internal server error", "upstream"],
    userMessage:
      "We encountered a temporary issue on the server. Please try again later.",
  },
  {
    keywords: ["network error", "fetch failed", "connection refused"],
    userMessage:
      "We could not reach the server. Please check your internet connection.",
  },
  {
    keywords: ["abort", "aborted", "user aborted"],
    userMessage: "Request stopped by user.",
  },
];

const GENERIC_ERROR =
  "An unexpected error occurred. Please try again or contact support if the issue persists.";

export const getUserFriendlyError = (
  rawError: string | undefined | null,
): string => {
  if (!rawError) return "";

  const lowerRaw = rawError.toLowerCase();

  // Find the first matching mapping
  const match = ERROR_LIBRARY.find((mapping) =>
    mapping.keywords.some((keyword) => lowerRaw.includes(keyword)),
  );

  return match
    ? match.userMessage
    : `${GENERIC_ERROR} (Ref: ${rawError.substring(0, 50)}...)`;
};
